// Generic project card — link tile used on /, /<owner> dashboards,
// and the home ProjectsSection. Routes to `/<owner>/<key>` for the
// product canvas; the "Open in Websites" shortcut on hover routes to
// `/studio/<owner>/<ws>/<key>`, the Websites board of the project's
// workspace.
//
// Inputs (DOMQL state):
//   s.owner       — string, org slug (preferred)
//   s.workspace   — object { slug, organization?: { slug } } (fallback shape
//                   that matches what sdk.getProjects returns)
//   s.key         — string, project key
//   s.name        — display name
//   s.description — short blurb
//   s.icon        — optional explicit icon URL (overrides screenshot)
//
// Empty-state sibling: `EmptyProjectCard` renders a dashed "+ Create
// your first project" placeholder when a workspace has no projects yet.
// Click invokes `el.call('openNewProjectPicker')` if registered;
// otherwise falls back to routing to `/add-app`.

import { projectScreenshotUrl } from '../functions/projects.js'
import { openNewProjectPickerModal } from '../functions/newProjectPicker.js'

export const ProjectCard = {
  // FA204 / frank trap — _gradientFor/_slugOf/_ownerOf/_wsSlugOf used to be
  // bare module-scope const/functions read from inside `background`/
  // `onClick` (serialized factories); they silently read as undefined once
  // this component is frank.toJSON'd for platform delivery (workspace
  // d6ea1142, project_frank_drops_module_local_helpers). Moved into `scope`,
  // called via `el.scope.<name>`; siblings call each other via `this.<name>`
  // (el.scope.foo() binds `this` to el.scope — object-literal properties
  // can't see each other as bare identifiers otherwise). EmptyProjectCard
  // below doesn't read any of these, so no duplication needed there.
  scope: {
    // `sdk.getProjects` returns `p.owner = { slug, id, type }` (an object),
    // so a naive `s.owner` would template-literal stringify to
    // "[object Object]" in the route URL. Always extract `.slug` from the
    // object shape; only fall back to the string-shaped tiers below.
    slugOf(v) {
      if (!v) return ''
      if (typeof v === 'string') return v
      return v.slug || v.key || ''
    },

    ownerOf(s) {
      return (
        this.slugOf(s?.owner) ||
        this.slugOf(s?.workspace?.organization) ||
        this.slugOf(s?.workspace) ||
        ''
      )
    },

    wsSlugOf(s) {
      return this.slugOf(s?.workspace) || this.slugOf(s?.workspaceSlug)
    }

    // No per-project gradient. It was a deterministic hue ramp seeded on
    // `owner/key`, which put an arbitrary saturated colour on every card and
    // gave the widget a different visual language from /studio, where the same
    // projects render as flat `canvas-card-glass` tiles (Nika 2026-08-16:
    // "remove that gradient styling and match it with /studio page"). It also
    // fought the design direction's one-hue-per-screen rule: a grid of eight
    // projects meant eight unrelated hues competing on one board.
    //
    // A project with no screenshot (or one that fails to load) shows its
    // initials on the neutral solid plate (Cover below), so cards differ by
    // their CONTENT (poster, initials, name, meta) rather than by decoration.
  },
  tag: 'button',
  type: 'button',
  flow: 'y',
  gap: '0',
  // Same silhouette + plate as a /studio ProjectNode, so the two surfaces read
  // as one family: radiusControl, canvas-card-glass, the 1px `line` border that
  // is the studio family standard, and NO drop shadow (studios.js strips it too
  // — "flat tiles read cleaner", Nika 2026-06-05).
  round: 'radiusControl',
  theme: 'canvas-card-glass',
  border: '1px solid line',
  // `canvas-card-glass` ships a drop shadow; /studio strips it on its own
  // ProjectNode (studios.js: "flat tiles read cleaner against the dark
  // canvas") and the design direction budgets one shadow per screen. Measured
  // before this line: the card rendered `rgba(0,0,0,0.35) 0 4px 16px` while a
  // studio node rendered none — so separation comes from the `line` border and
  // the fill contrast, not from lift.
  boxShadow: 'none',
  overflow: 'hidden',
  cursor: 'pointer',
  textAlign: 'left',
  padding: '0',
  position: 'relative',
  // The card itself no longer lifts/changes border on hover. The hover
  // affordance lives on the poster (Cover) instead — it zooms slightly.
  ':hover > [data-studio-btn]': { opacity: '1' },
  ':hover > [data-cover]': { transform: 'scale(1.04)' },
  // Touch has no hover, and smbls e3333d298 gates every emitted `:hover` rule
  // behind `@media (hover: hover)` — so on a phone the Studio button would sit
  // at its rest opacity: 0 forever and the only route into a project's studio
  // from the card would be gone. Reveal it on coarse pointers. The Cover zoom
  // stays hover-only: it is decoration, not an affordance.
  '@coarse': {
    '> [data-studio-btn]': { opacity: '1' }
  },

  onClick: (e, el, s) => {
    // Ignore clicks that came from the Studio button (which stops
    // propagation but we double-guard via the data attribute).
    if (e.target?.closest?.('[data-studio-btn]')) return
    const owner = el.scope.ownerOf(s)
    const key = s.key
    if (!owner || !key) return
    el.router(`/${owner}/${key}`, el.getRoot())
  },

  Cover: {
    dataCover: true,
    aspectRatio: '16 / 9',
    width: '100%',
    // Rounded poster with a small gap below so all four corners read as
    // rounded; zoom on card hover (driven by the parent ':hover' rule).
    round: 'radiusControl',
    overflow: 'hidden',
    marginBottom: 'V',
    transition: '260ms defaultBezier transform',
    // THE FALLBACK UNDER THE PICTURE (Fly QA 2026-10-18, D6): the cover
    // screenshot 404s for most projects (screenshots.api.symbols.app — 6 of 7
    // probed) and the tile was a blank plate. The cover is now two layers: the
    // project's initials on the solid card plate, and OVER them the picture
    // (CoverShot). A picture that loads covers the initials; one that fails
    // paints nothing, so the initials show — no load event, no state.
    position: 'relative',
    display: 'flex',
    align: 'center center',
    background: 'cardSolid',
    CoverInitials: {
      tag: 'span',
      ariaHidden: 'true',
      fontSize: 'D',
      fontWeight: '700',
      lineHeight: '1',
      color: 'caption',
      text: (el, s) =>
        String((s && (s.name || s.key)) || '')
          .split(/[\s._-]+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((w) => w.charAt(0).toUpperCase())
          .join('')
    },
    CoverShot: {
      position: 'absolute',
      inset: '0',
      background: (el, s) => {
        const owner = el.scope.ownerOf(s)
        const key = (s && s.key) || ''
        if (s && s.icon) return `center / cover no-repeat url("${s.icon}")`
        if (owner && key) {
          return `url("${projectScreenshotUrl({ owner, key })}") center / cover no-repeat`
        }
        return 'none'
      }
    }
  },

  Body: {
    flow: 'y',
    gap: 'V',
    padding: 'Z A',

    Name: {
      tag: 'h4',
      margin: '0',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.name || s.key || el.call('polyglot', 'editor.project.untitled')
    },

    Desc: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y',
      color: 'caption',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.description || (s.key ? `/${s.key}` : '')
    }
  },

  // Hover-revealed shortcut to the Websites board (the /studio view). Lives
  // over the Cover (top-right) so it doesn't fight the Body layout.
  // THE APP SPLIT (docs/APP_SPLIT.md vocabulary): it read "Apps" — the HUB's
  // word (shell.app.studio, the navbar's) — on every website card (dev Fly
  // "Websites" list, 2026-10-21). It now names its own action, so the label
  // is also the button's accessible name and no tooltip repeats it. The old
  // aria-label and title were one-shot el.call reads that kept the boot
  // language after a switch; the label's template follows the language.
  StudioBtn: {
    tag: 'button',
    type: 'button',
    dataStudioBtn: true,
    position: 'absolute',
    top: 'Z',
    right: 'Z',
    padding: 'V Z',
    round: 'radiusControl',
    // Longhand on purpose: colour tokens inside the `border` shorthand are
    // not resolved (the shorthand token-resolution trap).
    // Reviewed (fixed-colour sweep): kept — this glass pill floats over the
    // Cover (an arbitrary screenshot or per-project gradient), so it needs a
    // guaranteed-contrast fixed scrim in both themes (case 1: control over
    // arbitrary imagery), like TutorialsRowItem's play-button backdrop.
    borderWidth: 'hairline',
    borderStyle: 'solid',
    borderColor: 'white.18',
    background: 'black.55',
    color: 'white',
    fontSize: 'Y2',
    fontWeight: '700',
    cursor: 'pointer',
    opacity: '0',
    transition: '160ms defaultBezier opacity, 160ms defaultBezier background',
    ':hover': { background: 'black.78' },
    // Pressed: one more step of the same scrim (Rule 65).
    ':active': { background: 'black.92' },
    // It rests at opacity 0 and only the card's hover revealed it, so a
    // keyboard focus landed on nothing. The brand reset paints the ring.
    ':focus-visible': { opacity: '1' },
    flow: 'x',
    align: 'center',
    gap: 'V',
    Label: {
      tag: 'span',
      whiteSpace: 'nowrap',
      text: '{{ editor.project.openInWebsites | polyglot }}'
    },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      const owner = el.scope.ownerOf(s)
      const key = s.key
      if (!owner) return
      const wsSlug = el.scope.wsSlugOf(s)
      const parts = ['/studio', owner]
      if (wsSlug) parts.push(wsSlug)
      if (key) parts.push(key)
      el.router(parts.join('/'), el.getRoot())
    }
  }
}

// Empty-state companion — the dashed "+" tile shown when a workspace
// has no projects yet. Same footprint as ProjectCard so it slots into
// the same grid without a layout hop. Click invokes the new-project
// picker if registered (studios.js exposes one); otherwise routes to
// `/add-app` as the safe fallback.
export const EmptyProjectCard = {
  // Same footprint as ProjectCard so it slots cleanly into the same
  // grid: Cover spans 16/9, Body sits below — no auto-stretch to the
  // full row height.
  tag: 'button',
  type: 'button',
  flow: 'y',
  gap: '0',
  round: 'radiusControl',
  border: 'hairline dashed line',
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  padding: '0',
  alignSelf: 'flex-start',
  color: 'caption',
  overflow: 'hidden',
  transition: '180ms defaultBezier border-color, 180ms defaultBezier color',
  ':hover': { borderColor: 'blue', color: 'title' },

  Cover: {
    aspectRatio: '16 / 9',
    width: '100%',
    // When the card carries a FIXED height (the home Projects widget's
    // AddCard pins 200px + overflow:hidden), a wide grid column makes
    // 16/9 of the width + the Body exceed the card — and the Body's
    // bottom padding got clipped. Let the cover compress (flexShrink +
    // minHeight 0) so the Body always fits; in unconstrained usage the
    // aspect ratio still wins.
    flexShrink: '1',
    minHeight: '0',
    flow: 'y',
    align: 'center center',
    gap: 'V',
    background: 'overlay',
    Plus: {
      width: 'B',
      height: 'B',
      round: '50%',
      align: 'center center',
      background: 'dialog',
      color: 'title',
      fontSize: 'B',
      fontWeight: '300',
      text: '+'
    }
  },

  Body: {
    flow: 'y',
    gap: 'V',
    padding: 'Z A',
    // Never clipped in fixed-height cards — the Cover above compresses
    // instead (see its flexShrink note).
    flexShrink: '0',
    Title: {
      tag: 'span',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      text: '{{ editor.project.createFirst | polyglot }}'
    },
    Hint: {
      tag: 'span',
      fontSize: 'Y',
      color: 'caption',
      text: '{{ editor.project.inThisWorkspace | polyglot }}'
    }
  },

  // Open the NewProjectPickerBody @symbo.ls/windows modal — same shape as
  // the inline `openNewProjectPicker` helper in
  // canvas/components/StudioNavbar.js: seed a fresh `root.newProjectPicker`
  // carrier the body reads via s.root, then push the custom-template layer
  // in the shared ModalRoot. onClose (fires on X / backdrop / ESC /
  // closeModal) clears the carrier on any dismiss path.
  onClick: (e, el, s) => {
    e.preventDefault()
    openNewProjectPickerModal(el, s.root)
  }
}
